// Lib
import React, { MutableRefObject, ReactElement, RefObject } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Utils
import { getImageSource, getResponsiveImageSize } from '../../../element/image/imageHelper';
import { getImageTypeForElementType } from '../../../../common/media/mediaUtils';
import { getElementId, getElementType, getImageProp } from '../../../../common/elements/utils/elementPropertyUtils';

// Selectors
import { getIsFeatureEnabledForCurrentUser } from '../../../element/feature/elementFeatureSelector';

// Types
import { MNElement } from '../../../../common/elements/elementModelTypes';

// Constants
import { ExperimentId } from '../../../../common/experiments/experimentsConstants';

// Style
import './FocusModeHighQualityImageRenderer.scss';

const mapStateToProps = createStructuredSelector({
    // @ts-ignore Not sure why this is complaining...
    renderSecureImagesThroughCF: getIsFeatureEnabledForCurrentUser(ExperimentId.renderSecureImagesThroughCF),
});

const connector = connect(mapStateToProps);

type FocusModeHighQualityImageRendererProps = ConnectedProps<typeof connector> & {
    renderHighQualityImageCallbackRef: MutableRefObject<Function>;
    focusModeElementRef: RefObject<HTMLDivElement>;
    element: MNElement;
    renderSecureImagesThroughCF: boolean;
};

/**
 * Renders a high quality image over the top of the rendered image within a focus mode element.
 * We need this because Safari only scales the smaller image, rather than rendering a high pixel version
 * when scaled, so it would look pixelated otherwise.
 */
const FocusModeHighQualityImageRenderer = (props: FocusModeHighQualityImageRendererProps): ReactElement => {
    const { renderHighQualityImageCallbackRef, focusModeElementRef, element, renderSecureImagesThroughCF } = props;

    const imgRef = React.useRef<HTMLImageElement>(null);

    /**
     * Either renders a high quality image
     */
    renderHighQualityImageCallbackRef.current = React.useCallback((newShowImage: boolean) => {
        if (!imgRef.current || !focusModeElementRef.current) return;

        const imageNode: HTMLImageElement | null = focusModeElementRef.current.querySelector('.image-node');

        if (!imageNode) return;

        if (newShowImage) {
            const boundingRect = imageNode.getBoundingClientRect();

            const imageDetails = getImageProp(element);
            const imageType = getImageTypeForElementType(getElementType(element));
            const imageSize = getResponsiveImageSize({
                widthPx: boundingRect.width || Infinity,
                heightPx: boundingRect.height || Infinity,
                imageDetails,
                imageType,
            });

            // @ts-ignore Currying
            const imageSource = getImageSource({
                imageSize,
                imageDetails,
                elementId: getElementId(element),
                useSecureMediaUrl: true,
                renderSecureImagesThroughCF,
            });

            // We have to first pre-load the image for some reason. Safari struggles to render
            // the whole image sometimes if it's high-res and not loaded already when it comes
            // time to display it
            const imagePreloader = new Image();
            imagePreloader.src = imageSource;
            imagePreloader.decoding = 'sync';

            imagePreloader.onload = () => {
                if (!imgRef.current) return;

                // allow drawing image
                imgRef.current.src = imageSource;
                imgRef.current.width = boundingRect.width;
                imgRef.current.height = boundingRect.height;
                imgRef.current.style.top = `${boundingRect.top}px`;
                imgRef.current.style.left = `${boundingRect.left}px`;
                imgRef.current.style.opacity = '1';
            };
        } else {
            imgRef.current.style.opacity = '0';
        }
    }, []);

    // Not sure if "decoding" is needed, but this answer seems to suggest it helps and it appeared to for me:
    //  https://stackoverflow.com/questions/66903058/why-does-safari-only-render-the-top-of-images-sometimes
    return <img ref={imgRef} decoding="sync" className="FocusModeHighQualityImageRenderer" />;
};

// @ts-ignore Not sure why this is complaining...
export default connector(FocusModeHighQualityImageRenderer);
